// One gate's queue time across the day, with gates open and game start marked.
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from 'chart.js';
import { ChartHeader, applyChartTheme, CHART_THEME, hoverGuidePlugin, sectionLineDataset } from '../../Concession/charts/chartTheme';
import { DATA_COLOR, PHASES } from '../../Retail/retailPalette';
import { minuteToClock, clockToMinute } from '../gatesMetrics';

ChartJS.register(annotationPlugin);

// Queues at the gates matter until shortly after the game starts
const SHOW_AFTER_START = 60;

const markAt = (minute, label, color) => ({
  type: 'line',
  scaleID: 'x',
  value: minute,
  borderColor: color,
  borderWidth: 1.5,
  borderDash: [6, 4],
  label: {
    display: true,
    content: label,
    position: 'start',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color,
    font: { family: CHART_THEME.font, size: 11, weight: '600' },
    padding: { x: 6, y: 2 },
  },
});

// `range` ({ start, end } in game-day minutes) limits the chart to the analysis window.
// `afterStart`: without a window, stop this many minutes after game start (null shows the whole day).
// `who` names the place in the empty message ("gate", "restroom").
const GateQueueChart = ({ series, currentGame, range = null, height = 260, afterStart = SHOW_AFTER_START, who = 'gate' }) => {
  const gates = clockToMinute(currentGame?.gatesOpen);
  const start = clockToMinute(currentGame?.gameStartTime);

  const points = useMemo(() => {
    // With an analysis window the window decides; otherwise stop an hour after the game starts
    const cutoff = !range && start != null && afterStart != null ? start + afterStart : null;
    return (series || [])
      .filter((p) => cutoff == null || p.minute <= cutoff)
      .filter((p) => !range || (p.minute >= range.start && p.minute <= range.end))
      .map((p) => ({ x: p.minute, y: p.queueTime }));
  }, [series, start, range, afterStart]);

  const first = points.length ? points[0].x : 0;
  const last = points.length ? points[points.length - 1].x : 0;
  const inRange = (m) => m != null && m >= first && m <= last;

  const options = useMemo(() => applyChartTheme({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        min: first,
        max: last,
        grid: { display: false },
        ticks: {
          stepSize: 60,
          maxRotation: 0,
          callback: (v) => (v % 60 === 0 ? minuteToClock(v).replace(':00 ', ' ') : ''),
        },
        afterBuildTicks: (scale) => {
          const ticks = [];
          for (let m = Math.ceil(first / 60) * 60; m <= last; m += 60) ticks.push({ value: m });
          scale.ticks = ticks;
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 10,
        ticks: { precision: 0, callback: (v) => `${v}m` },
        title: { display: true, text: 'Queue (min)' },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => minuteToClock(items[0]?.raw?.x),
          label: (ctx) => ` Queue: ${Math.round(ctx.raw.y)} min`,
        },
      },
      annotation: {
        annotations: [
          ...(inRange(gates) ? [markAt(gates, 'Gates open', PHASES.gates.color)] : []),
          ...(inRange(start) ? [markAt(start, 'Game start', CHART_THEME.gameStart)] : []),
        ],
      },
    },
  }), [first, last, gates, start]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => ({
    datasets: [{ label: 'Queue', data: points, ...sectionLineDataset(DATA_COLOR) }],
  }), [points]);

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title="Queue time"
        subtitle={!range && start != null && afterStart != null ? 'Through the hour after the game starts. When a minute has two readings, the later one is shown.' : 'When a minute has two readings, the later one is shown.'}
      />
      {points.length ? (
        <div style={{ position: 'relative', height, marginTop: 8 }}>
          <Line data={data} options={options} plugins={[hoverGuidePlugin]} />
        </div>
      ) : (
        <div className="gx-chart-empty">{range ? `No queue readings for this ${who} in the selected window.` : `No queue readings for this ${who}.`}</div>
      )}
    </div>
  );
};

export default GateQueueChart;
